import Decimal from 'decimal.js-light';
import type { Infer } from 'superstruct';
import {
	array,
	boolean,
	coerce,
	dynamic,
	never,
	nullable,
	number,
	object,
	optional,
	partial,
	refine,
	string,
} from 'superstruct';

import {
	minimumValidatedArray,
	parsableInteger,
	stringDate,
	userString,
	withMessage,
} from '@adflow/shared/util-superstruct';

import {
	AddFacebookAdTemplateDto,
	ViewFacebookAdTemplateDto,
} from './facebook-ad-template.dto';
import {
	AddFacebookAdSetBudgetTemplateDto,
	ViewFacebookAdSetBudgetTemplateDto,
} from './facebook-adset-budget-template.dto';
import {
	AddFacebookAdSetLocationTemplateDto,
	ViewFacebookAdSetLocationTemplateDto,
} from './facebook-adset-location-template.dto';

export const FacebookBehavior = object({
	id: parsableInteger(),
	name: string(),
});

export type FacebookBehavior = Infer<typeof FacebookBehavior>;

export const FacebookInterest = object({
	id: parsableInteger(),
	name: string(),
});

export type FacebookInterest = Infer<typeof FacebookInterest>;

function isCatalogAdSetContext(context: { branch: unknown[] }) {
	const campaignTemplate = context.branch.at(-4) as
		{ useProductCatalog?: boolean } | undefined;
	return campaignTemplate?.useProductCatalog === true;
}

export const ConversionEventValidation = dynamic((value, context) => {
	const currentAdSet = context.branch.at(-2);
	const campaignTemplate = context.branch.at(-4);
	const campaignObjective = campaignTemplate?.objective;
	const optimizationGoal = currentAdSet?.optimizationGoal;

	if (
		campaignObjective &&
		typeof campaignObjective === 'string' &&
		optimizationGoal &&
		typeof optimizationGoal === 'string'
	) {
		const availableConversionEvents = getConversionEvents(
			campaignObjective as CampaignObjective,
			optimizationGoal
		);

		if (availableConversionEvents.length === 0) {
			return optional(nullable(string()));
		}

		const isRequired = isConversionEventRequired(
			campaignObjective as CampaignObjective,
			optimizationGoal
		);

		if (isRequired && (!value || typeof value !== 'string')) {
			return withMessage(string(), 'validations:Conversion Event Required');
		}

		if (
			value &&
			typeof value === 'string' &&
			!isValidConversionEvent(
				campaignObjective as CampaignObjective,
				optimizationGoal,
				value
			)
		) {
			return withMessage(never(), 'validations:Invalid Conversion Event');
		}
	}

	return optional(nullable(string()));
});

export const DestinationTypeValidation = dynamic((value, context) => {
	if (isCatalogAdSetContext(context)) {
		return optional(nullable(string()));
	}

	const currentAdSet = context.branch.at(-2);
	const campaignTemplate = context.branch.at(-4);
	const campaignObjective = campaignTemplate?.objective;
	const optimizationGoal = currentAdSet?.optimizationGoal;

	if (
		campaignObjective &&
		typeof campaignObjective === 'string' &&
		optimizationGoal &&
		typeof optimizationGoal === 'string'
	) {
		const availableConversionLocations = getConversionLocations(
			campaignObjective as CampaignObjective,
			optimizationGoal
		);

		if (availableConversionLocations.length === 0) {
			return optional(nullable(string()));
		}

		if (
			value &&
			typeof value === 'string' &&
			!isValidConversionLocation(
				campaignObjective as CampaignObjective,
				optimizationGoal,
				value
			)
		) {
			return withMessage(never(), 'validations:Invalid Conversion Location');
		}

		if (!value || typeof value !== 'string') {
			return withMessage(string(), 'validations:Required');
		}

		return string();
	}

	return optional(nullable(string()));
});

export const BillingEventValidation = dynamic((value, context) => {
	const currentAdSet = context.branch.at(-2);
	const campaignTemplate = context.branch.at(-4);
	const campaignObjective = campaignTemplate?.objective;
	const optimizationGoal = currentAdSet?.optimizationGoal;

	if (
		campaignObjective &&
		typeof campaignObjective === 'string' &&
		optimizationGoal &&
		typeof optimizationGoal === 'string'
	) {
		const availableBillingEvents = getBillingEvents(
			campaignObjective as CampaignObjective,
			optimizationGoal
		);

		if (availableBillingEvents.length === 0) {
			return optional(nullable(string()));
		}

		if (
			value &&
			typeof value === 'string' &&
			!isValidBillingEvent(
				campaignObjective as CampaignObjective,
				optimizationGoal,
				value
			)
		) {
			return withMessage(never(), 'validations:Invalid Billing Event');
		}

		if (!value || typeof value !== 'string') {
			return withMessage(string(), 'validations:Required');
		}

		return string();
	}

	return optional(nullable(string()));
});

export const OptimizationGoalValidation = dynamic((value, context) => {
	const campaignTemplate = context.branch.at(-4);
	const campaignObjective = campaignTemplate?.objective;
	const campaignBidStrategy = campaignTemplate?.bidStrategy;

	if (
		campaignTemplate?.budgetStrategy === 'CAMPAIGN_BUDGET' &&
		campaignBidStrategy === 'LOWEST_COST_WITH_MIN_ROAS' &&
		value !== 'VALUE'
	) {
		return withMessage(never(), 'validations:ROAS Optimization Goal Required');
	}

	if (campaignObjective && typeof campaignObjective === 'string') {
		const availableOptimizationGoals = getOptimizationGoals(
			campaignObjective as CampaignObjective
		);

		if (availableOptimizationGoals.length === 0) {
			return optional(nullable(string()));
		}

		if (
			value &&
			typeof value === 'string' &&
			!isValidOptimizationGoal(campaignObjective as CampaignObjective, value)
		) {
			return withMessage(never(), 'validations:Invalid Optimization Goal');
		}

		if (!value || typeof value !== 'string') {
			return withMessage(string(), 'validations:Required');
		}

		return string();
	}

	return optional(nullable(string()));
});

export const BudgetAmountCoercion = coerce(number(), string(), (value) => {
	if (!value) return 0;
	try {
		return Math.max(new Decimal(value).toNumber(), 0);
	} catch {
		return 0;
	}
});

export const minAge = 18;
export const maxAge = 65;

export const AddFacebookAdSetTemplateDto = object({
	id: optional(parsableInteger()),
	name: userString('validations:Required'),
	optimizationGoal: OptimizationGoalValidation,
	status: userString('validations:Required'),

	billingEvent: BillingEventValidation,
	budgetType: dynamic((value, context) => {
		const campaignTemplate = context.branch.at(-4);
		if (campaignTemplate?.budgetStrategy === 'CAMPAIGN_BUDGET') {
			return optional(nullable(string()));
		}
		return withMessage(string(), 'validations:Required');
	}),
	budgetAmount: dynamic((value, context) => {
		const campaignTemplate = context.branch.at(-4);
		if (campaignTemplate?.budgetStrategy === 'CAMPAIGN_BUDGET') {
			return optional(nullable(BudgetAmountCoercion));
		}

		return withMessage(
			refine(BudgetAmountCoercion, 'aboveZero', (v) => v > 0),
			'validations:Required'
		);
	}),
	bidStrategy: optional(nullable(string())),
	bidAmount: optional(nullable(number())),
	budgetStartDate: dynamic((value, context) => {
		if (value && typeof value === 'string') {
			const contextValues = context.branch.at(-2);
			const budgetEndDate = contextValues.budgetEndDate;
			const isGreater = new Date(value) >= new Date(budgetEndDate);
			if (isGreater) {
				return withMessage(
					never(),
					'validations:DateTime startDateTimeBeforeEnd'
				);
			}
		}
		return withMessage(stringDate(), 'validations:Required');
	}),
	budgetEndDate: withMessage(stringDate(), 'validations:Required'),
	destinationType: DestinationTypeValidation,
	conversionEvent: ConversionEventValidation,

	ageMax: optional(parsableInteger()),
	ageMin: dynamic((value) => {
		const ageMin = Number(value);
		if (value && ageMin && typeof ageMin === 'number') {
			const isBetween = ageMin < minAge || ageMin > maxAge;
			if (isBetween) {
				return withMessage(never(), 'validations:Between');
			}
		}
		return withMessage(parsableInteger(), 'validations:Required');
	}),
	gender: optional(string()),
	interests: array(FacebookInterest),
	behaviors: array(FacebookBehavior),
	platforms: array(string()),
	platformPositions: minimumValidatedArray(
		array(string()),
		userString('validations:Required'),
		1
	),
	placementSoftOptOut: optional(array(string())),

	// Advantage+ targeting fields
	advantageAudience: optional(boolean()),
	advantageCustomAudience: optional(boolean()),

	adSetBudgetTemplates: optional(array(AddFacebookAdSetBudgetTemplateDto)),
	adSetLocationTemplates: optional(array(AddFacebookAdSetLocationTemplateDto)),

	adTemplates: optional(array(AddFacebookAdTemplateDto)),
});

export const ViewFacebookAdSetTemplateDto = object({
	id: number(),
	name: string(),
	optimizationGoal: string(),
	status: string(),
	createdAt: stringDate(),
	updatedAt: stringDate(),

	billingEvent: string(),
	budgetType: nullable(string()),
	budgetAmount: coerce(number(), nullable(number()), (value) => value ?? 0),
	bidStrategy: nullable(string()),
	bidAmount: nullable(number()),
	budgetStartDate: stringDate(),
	budgetEndDate: stringDate(),
	destinationType: string(),
	conversionEvent: nullable(string()),

	ageMax: nullable(number()),
	ageMin: nullable(number()),
	gender: nullable(string()),
	interests: array(FacebookInterest),
	behaviors: array(FacebookBehavior),
	platforms: array(string()),
	platformPositions: array(string()),
	placementSoftOptOut: optional(array(string())),

	// Advantage+ targeting fields
	advantageAudience: optional(boolean()),
	advantageCustomAudience: optional(boolean()),

	adSetBudgetTemplates: optional(array(ViewFacebookAdSetBudgetTemplateDto)),
	adSetLocationTemplates: optional(array(ViewFacebookAdSetLocationTemplateDto)),
	adTemplates: optional(array(ViewFacebookAdTemplateDto)),

	adSets: optional(
		array(
			object({
				id: number(),
				facebookId: string(),
				syncedAt: stringDate(),
			})
		)
	),
});

export type ViewFacebookAdSetTemplateDto = Infer<
	typeof ViewFacebookAdSetTemplateDto
>;
export type AddFacebookAdSetTemplateDto = Infer<
	typeof AddFacebookAdSetTemplateDto
>;
export const EditFacebookAdSetTemplateDto = partial(
	AddFacebookAdSetTemplateDto
);
export type EditFacebookAdSetTemplateDto = Infer<
	typeof EditFacebookAdSetTemplateDto
>;

// Reference: https://developers.facebook.com/docs/marketing-api/reference/ad-campaign-group/#odax
export const FACEBOOK_CAMPAIGN_OBJECTIVES = {
	OUTCOME_SALES: {
		optimization_goal: {
			OFFSITE_CONVERSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [
					'PURCHASE',
					'INITIATED_CHECKOUT',
					'ADD_PAYMENT_INFO',
					'ADD_TO_CART',
					'COMPLETE_REGISTRATION',
					'DONATE',
					'START_TRIAL',
					'SUBSCRIBE',
					'CONTENT_VIEW',
				],
			},
			VALUE: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [
					'PURCHASE',
					'INITIATED_CHECKOUT',
					'ADD_PAYMENT_INFO',
					'ADD_TO_CART',
					'COMPLETE_REGISTRATION',
					'DONATE',
					'START_TRIAL',
					'SUBSCRIBE',
					'CONTENT_VIEW',
				],
			},
			LANDING_PAGE_VIEWS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			IMPRESSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			REACH: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
		},
	},
	OUTCOME_LEADS: {
		optimization_goal: {
			OFFSITE_CONVERSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [
					'LEAD',
					'COMPLETE_REGISTRATION',
					'CONTACT',
					'FIND_LOCATION',
					'SCHEDULE',
					'START_TRIAL',
					'SUBMIT_APPLICATION',
					'SUBSCRIBE',
				],
			},
			LANDING_PAGE_VIEWS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			LINK_CLICKS: {
				billing_event: ['LINK_CLICKS', 'IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			IMPRESSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			REACH: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
		},
	},
	OUTCOME_TRAFFIC: {
		optimization_goal: {
			LANDING_PAGE_VIEWS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			LINK_CLICKS: {
				billing_event: ['LINK_CLICKS', 'IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			IMPRESSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			REACH: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			QUALITY_CALL: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['PHONE_CALL'],
				conversion_event: [],
			},
		},
	},
	OUTCOME_AWARENESS: {
		optimization_goal: {
			AD_RECALL_LIFT: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			REACH: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
			IMPRESSIONS: {
				billing_event: ['IMPRESSIONS'],
				conversion_location: ['WEBSITE'],
				conversion_event: [],
			},
		},
	},
} as const;

export type CampaignObjective = keyof typeof FACEBOOK_CAMPAIGN_OBJECTIVES;
export type OptimizationGoal<T extends CampaignObjective> =
	keyof (typeof FACEBOOK_CAMPAIGN_OBJECTIVES)[T]['optimization_goal'];

export function getCampaignObjectives(): CampaignObjective[] {
	return Object.keys(FACEBOOK_CAMPAIGN_OBJECTIVES) as CampaignObjective[];
}

export function getOptimizationGoals(
	campaignObjective: CampaignObjective
): string[] {
	const objective = FACEBOOK_CAMPAIGN_OBJECTIVES[campaignObjective];
	if (!objective) return [];
	return Object.keys(objective.optimization_goal);
}

export function getBillingEvents(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
): readonly string[] {
	const objective = FACEBOOK_CAMPAIGN_OBJECTIVES[campaignObjective];
	if (!objective) return [];

	const goals = Object.entries(objective.optimization_goal);

	const goal = goals.find(([key]) => key === optimizationGoal);
	if (!goal) return [];

	return goal[1]?.billing_event ?? [];
}

export function getConversionLocations(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
): readonly string[] {
	const objective = FACEBOOK_CAMPAIGN_OBJECTIVES[campaignObjective];
	if (!objective) return [];

	const goals = Object.entries(objective.optimization_goal);
	const goal = goals.find(([key]) => key === optimizationGoal);
	if (!goal) return [];

	return goal[1]?.conversion_location ?? [];
}

export function getConversionEvents(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
): readonly string[] {
	const objective = FACEBOOK_CAMPAIGN_OBJECTIVES[campaignObjective];
	if (!objective) return [];

	const goals = Object.entries(objective.optimization_goal);
	const goal = goals.find(([key]) => key === optimizationGoal);
	if (!goal) return [];

	return goal[1]?.conversion_event ?? [];
}

export function isValidOptimizationGoal(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
): boolean {
	const availableGoals = getOptimizationGoals(campaignObjective);
	return availableGoals.includes(optimizationGoal);
}

export function isValidBillingEvent(
	campaignObjective: CampaignObjective,
	optimizationGoal: string,
	billingEvent: string
): boolean {
	const availableEvents = getBillingEvents(campaignObjective, optimizationGoal);
	return availableEvents.includes(billingEvent);
}

export function isValidConversionLocation(
	campaignObjective: CampaignObjective,
	optimizationGoal: string,
	conversionLocation: string
): boolean {
	const availableLocations = getConversionLocations(
		campaignObjective,
		optimizationGoal
	);
	return availableLocations.includes(conversionLocation);
}

export function isValidConversionEvent(
	campaignObjective: CampaignObjective,
	optimizationGoal: string,
	conversionEvent: string
): boolean {
	const availableEvents = getConversionEvents(
		campaignObjective,
		optimizationGoal
	);
	return availableEvents.includes(conversionEvent);
}

export function isConversionEventRequired(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
): boolean {
	const availableEvents = getConversionEvents(
		campaignObjective,
		optimizationGoal
	);
	return availableEvents.length > 0;
}

export function getAdSetConfiguration(
	campaignObjective: CampaignObjective,
	optimizationGoal: string
) {
	return {
		billingEvents: getBillingEvents(campaignObjective, optimizationGoal),
		conversionLocations: getConversionLocations(
			campaignObjective,
			optimizationGoal
		),
		conversionEvents: getConversionEvents(campaignObjective, optimizationGoal),
		conversionEventRequired: isConversionEventRequired(
			campaignObjective,
			optimizationGoal
		),
	};
}
